import { z } from 'zod';

// src/mock/random.ts
var seedState = 42;
function seedRandom(value) {
  seedState = (value ?? 42) >>> 0;
}
function nextFloat() {
  seedState = seedState * 1664525 + 1013904223 >>> 0;
  return seedState / 4294967296;
}
function randomInt({ min, max }) {
  return Math.floor(nextFloat() * (max - min + 1)) + min;
}
function randomFloat({
  min,
  max,
  fractionDigits = 2
}) {
  const value = nextFloat() * (max - min) + min;
  const factor = 10 ** fractionDigits;
  return Math.round(value * factor) / factor;
}
function randomBoolean() {
  return nextFloat() < 0.5;
}
function randomArrayElement(array) {
  return array[randomInt({ min: 0, max: array.length - 1 })];
}
function shuffleArray(array) {
  const copy = array.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randomInt({ min: 0, max: i });
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
  }
  return copy;
}
function randomPastDate({ years = 1 } = {}) {
  const now = Date.now();
  const maxAge = years * 365 * 24 * 60 * 60 * 1e3;
  const age = Math.floor(nextFloat() * maxAge);
  return new Date(now - age);
}
function randomRecentDate({ days = 30 } = {}) {
  const now = Date.now();
  const maxAge = days * 24 * 60 * 60 * 1e3;
  const age = Math.floor(nextFloat() * maxAge);
  return new Date(now - age);
}
function randomStraddlingDate({ days = 15 } = {}) {
  const now = Date.now();
  const span = days * 24 * 60 * 60 * 1e3;
  const offset = Math.floor(nextFloat() * span * 2) - span;
  return new Date(now + offset);
}
function randomAnytimeDate() {
  const now = Date.now();
  const maxAge = 100 * 365 * 24 * 60 * 60 * 1e3;
  const age = Math.floor(nextFloat() * maxAge);
  return new Date(now - age);
}
var LOREM_WORDS = [
  "harbor",
  "summit",
  "meridian",
  "atlas",
  "beacon",
  "cedar",
  "delta",
  "ember",
  "falcon",
  "garnet",
  "horizon",
  "indigo",
  "juniper",
  "keystone",
  "lantern",
  "meadow",
  "nimbus",
  "orchard",
  "pioneer",
  "quarry",
  "redwood",
  "sierra",
  "timber",
  "umber",
  "vista",
  "willow",
  "zenith",
  "anchor",
  "basalt",
  "canyon",
  "drift",
  "estuary",
  "fjord",
  "grove",
  "heath",
  "inlet",
  "jade",
  "knoll",
  "lagoon",
  "mesa",
  "north",
  "onyx",
  "prairie",
  "quartz",
  "ridge",
  "slate",
  "terrace",
  "upland",
  "valley",
  "wharf",
  "yonder",
  "aspen",
  "brook",
  "cove",
  "dune",
  "elm",
  "fern",
  "glen",
  "haven",
  "isle"
];
function randomWords(count) {
  const words = [];
  for (let i = 0; i < count; i++) {
    words.push(randomArrayElement(LOREM_WORDS));
  }
  return words.join(" ");
}
function randomSentence() {
  const words = randomWords(randomInt({ min: 4, max: 8 }));
  return words.charAt(0).toUpperCase() + words.slice(1) + ".";
}
function randomUuid() {
  const hex = () => randomInt({ min: 0, max: 15 }).toString(16);
  return `${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}-${hex()}${hex()}${hex()}${hex()}-4${hex()}${hex()}${hex()}-${hex()}${hex()}${hex()}${hex()}-${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}${hex()}`;
}
function randomColor() {
  const channel = () => randomInt({ min: 0, max: 255 }).toString(16).padStart(2, "0");
  return `#${channel()}${channel()}${channel()}`;
}
var PASSWORD_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
function randomPassword(length = 12) {
  let password = "";
  for (let i = 0; i < length; i++) {
    password += randomArrayElement(PASSWORD_CHARS.split(""));
  }
  return password;
}
function randomEmail() {
  const user = randomWords(1).toLowerCase().replace(/\s+/g, ".");
  const domain = randomWords(1).toLowerCase().replace(/\s+/g, "");
  return `${user}@${domain}.com`;
}
function randomUrl() {
  const slug2 = randomWords(2).toLowerCase().replace(/\s+/g, "-");
  return `https://example.com/${slug2}`;
}
function randomPhone() {
  const area = randomInt({ min: 200, max: 999 });
  const prefix = randomInt({ min: 200, max: 999 });
  const line = randomInt({ min: 0, max: 9999 }).toString().padStart(4, "0");
  return `+1 (${area}) ${prefix}-${line}`;
}

// src/access/entityAccess.ts
function orbitalInlineEntities(orbital) {
  const out = [];
  const refs = [orbital.entity, ...orbital.auxiliaryEntities ?? []];
  for (const ref of refs) {
    if (typeof ref === "object" && ref !== null && "fields" in ref) {
      out.push(ref);
    }
  }
  return out;
}
function inlineEntities(schema) {
  return (schema.orbitals ?? []).flatMap(orbitalInlineEntities);
}
function isDeclared(p) {
  return p.read !== void 0 || p.create !== void 0 || p.update !== void 0 || p.delete !== void 0;
}
function entityAccessTable(schema) {
  const table = /* @__PURE__ */ new Map();
  const collectionOf = /* @__PURE__ */ new Map();
  for (const def of inlineEntities(schema)) {
    table.set(def.name, {
      read: def.read_policy,
      create: def.create_policy,
      update: def.update_policy,
      delete: def.delete_policy
    });
    if (def.collection) collectionOf.set(def.name, def.collection);
  }
  const byCollection = /* @__PURE__ */ new Map();
  for (const [name, policies] of table) {
    const collection = collectionOf.get(name);
    if (collection && isDeclared(policies) && !byCollection.has(collection)) {
      byCollection.set(collection, policies);
    }
  }
  for (const [name, policies] of table) {
    if (isDeclared(policies)) continue;
    const collection = collectionOf.get(name);
    const inherited = collection ? byCollection.get(collection) : void 0;
    if (inherited) table.set(name, { ...inherited });
  }
  return table;
}
function entityAccessPolicies(schema, entityName) {
  return entityAccessTable(schema).get(entityName);
}
function entityAccessPoliciesByStoreKey(schema) {
  const collectionOf = /* @__PURE__ */ new Map();
  for (const def of inlineEntities(schema)) {
    if (def.collection) collectionOf.set(def.name, def.collection);
  }
  const byKey = /* @__PURE__ */ new Map();
  for (const [name, policies] of entityAccessTable(schema)) {
    const key = collectionOf.get(name) ?? name.toLowerCase();
    if (!byKey.has(key) && isDeclared(policies)) byKey.set(key, policies);
  }
  return byKey;
}

// src/mock/identityOwners.ts
function inlineEntities2(schema) {
  return (schema.orbitals ?? []).flatMap(orbitalInlineEntities);
}
function identityEntitiesTaggedOf(orbitals) {
  const out = [];
  for (const orbital of orbitals) {
    const ref = orbital.entity;
    if (typeof ref === "object" && ref !== null && "fields" in ref && ref.identity === true) {
      out.push({ def: ref, primary: true });
    }
  }
  for (const orbital of orbitals) {
    for (const ref of orbital.auxiliaryEntities ?? []) {
      if (typeof ref === "object" && ref !== null && "fields" in ref && ref.identity === true) {
        out.push({ def: ref, primary: false });
      }
    }
  }
  return out;
}
function identityEntitiesOf(orbitals) {
  const tagged = identityEntitiesTaggedOf(orbitals);
  const hasPrimary = tagged.some((e) => e.primary);
  return tagged.filter((e) => e.primary || !hasPrimary).map((e) => e.def);
}
function identityEntityName(schema) {
  return identityEntitiesOf(schema.orbitals ?? [])[0]?.name;
}
function identityEntityNames(schema) {
  const out = [];
  for (const { def } of identityEntitiesTaggedOf(schema.orbitals ?? [])) {
    if (!out.includes(def.name)) out.push(def.name);
  }
  return out;
}
function roleVocabularyOf(entity, field = "role") {
  const def = entity.fields.find((f) => f.name === field);
  if (!def) return void 0;
  return fieldVocabulary(def);
}
function fieldVocabulary(field) {
  if ("values" in field && field.values && field.values.length > 0) {
    return field.values;
  }
  if ("items" in field && field.items && "values" in field.items && field.items.values && field.items.values.length > 0) {
    return field.items.values;
  }
  return void 0;
}
function fieldRefName(x, binding) {
  if (typeof x === "string" && x.startsWith(`${binding}.`)) return x.slice(binding.length + 1);
  if (Array.isArray(x) && x.length === 3 && x[0] === "object/get" && x[1] === binding && typeof x[2] === "string") {
    return x[2];
  }
  return void 0;
}
function isFieldRef(x, binding, field) {
  return fieldRefName(x, binding) === field;
}
var OWNER_EQ_OPS = ["=", "=="];
function ownerColumnsFromPolicy(expr, out = []) {
  if (Array.isArray(expr)) {
    if (expr.length === 3) {
      const [op, a, b] = expr;
      if (typeof op === "string" && OWNER_EQ_OPS.includes(op)) {
        const field = isFieldRef(b, "@user", "id") ? fieldRefName(a, "@entity") : isFieldRef(a, "@user", "id") ? fieldRefName(b, "@entity") : void 0;
        if (field !== void 0 && !out.includes(field)) out.push(field);
      }
    }
    for (const item of expr) ownerColumnsFromPolicy(item, out);
  }
  return out;
}
function policiesSelfIdentify(policies) {
  if (policies === void 0) return false;
  return [policies.read, policies.create, policies.update, policies.delete].some(
    (expr) => expr !== void 0 && ownerColumnsFromPolicy(expr).includes("id")
  );
}
function ownerFieldsFromSchema(schema) {
  const identities = identityEntityNames(schema);
  if (identities.length === 0) return [];
  const out = [];
  const defs = inlineEntities2(schema);
  const declaredByCollection = /* @__PURE__ */ new Map();
  for (const def of defs) {
    for (const field of def.fields ?? []) {
      if (field.name && field.type === "relation" && !field.intrinsic && identities.includes(field.relation.entity)) {
        out.push(`${def.name}.${field.name}`);
        if (def.collection) {
          const cols = declaredByCollection.get(def.collection) ?? [];
          if (!cols.includes(field.name)) cols.push(field.name);
          declaredByCollection.set(def.collection, cols);
        }
      }
    }
  }
  for (const name of identities) {
    if (policiesSelfIdentify(entityAccessPolicies(schema, name))) {
      const key = `${name}.id`;
      if (!out.includes(key)) out.push(key);
    }
  }
  for (const def of defs) {
    const policies = entityAccessPolicies(schema, def.name);
    if (policies === void 0) continue;
    for (const policy of [policies.read, policies.update, policies.delete]) {
      if (policy === void 0) continue;
      for (const field of ownerColumnsFromPolicy(policy)) {
        const key = `${def.name}.${field}`;
        if (!out.includes(key)) out.push(key);
      }
    }
  }
  for (const def of defs) {
    const cols = def.collection ? declaredByCollection.get(def.collection) : void 0;
    if (!cols) continue;
    for (const col of cols) {
      const key = `${def.name}.${col}`;
      if (out.includes(key)) continue;
      if ((def.fields ?? []).some((f) => f.name === col)) out.push(key);
    }
  }
  return out;
}
var EQUALITY_OPS = ["=", "==", "!=", "!==", "eq", "neq"];
function isUserFieldSigil(x) {
  return typeof x === "string" && fieldRefName(x, "@user") !== void 0;
}
function literalHaystackMembers(items) {
  const isListCall = items[0] === "list";
  const members = isListCall ? items.slice(1) : items;
  return members.every((v) => typeof v === "string") ? [...members] : void 0;
}
function collectUserFieldLiterals(expr, out = /* @__PURE__ */ new Map()) {
  if (Array.isArray(expr)) {
    const [op, ...args] = expr;
    if (typeof op === "string" && args.length >= 2 && (EQUALITY_OPS.includes(op) || op === "array/includes")) {
      const isIncludes = op === "array/includes";
      let sigilSide;
      let literalSide;
      if (isIncludes) {
        if (isUserFieldSigil(args[0])) {
          sigilSide = args.slice(0, 1);
          literalSide = args.slice(1, 2);
        } else {
          sigilSide = args.slice(1, 2);
          literalSide = args.slice(0, 1);
        }
      } else {
        sigilSide = args;
        literalSide = args;
      }
      const field = sigilSide.map((arg) => fieldRefName(arg, "@user")).find((f) => f !== void 0);
      if (field !== void 0) {
        const literalArrayHaystack = literalSide[0];
        const literals = isIncludes && Array.isArray(literalArrayHaystack) ? literalHaystackMembers(literalArrayHaystack) ?? [] : literalSide;
        for (const literal of literals) {
          if (typeof literal === "string" && !literal.startsWith("@")) {
            const set = out.get(field) ?? /* @__PURE__ */ new Set();
            set.add(literal);
            out.set(field, set);
          }
        }
      }
    }
    for (const item of expr) collectUserFieldLiterals(item, out);
    return out;
  }
  if (expr !== null && typeof expr === "object") {
    for (const value of Object.values(expr)) collectUserFieldLiterals(value, out);
  }
  return out;
}
function roleSatisfyingPolicy(policy, identity, field = "role") {
  if (policy === void 0 || policy === null) return void 0;
  const vocab = roleVocabularyOf(identity, field);
  if (vocab === void 0) return void 0;
  const accepted = collectUserFieldLiterals(policy).get(field);
  if (accepted === void 0) return void 0;
  return vocab.find((value) => accepted.has(value));
}
var ID_PREFIXES = {
  orbital: "orb_",
  entity: "ent_",
  trait: "trt_",
  event: "evt_",
  page: "pag_",
  service: "svc_",
  theme: "thm_",
  palette: "pal_"
};
function brand(value) {
  return value;
}
function makeIdKind(kind) {
  const prefix = ID_PREFIXES[kind];
  const is = (value) => value.startsWith(prefix) && value.length > prefix.length;
  const as = (value) => {
    if (!is(value)) {
      throw new Error(
        `Expected ${kind} id (prefix "${prefix}"), got: ${JSON.stringify(value)}`
      );
    }
    return brand(value);
  };
  return { prefix, is, as };
}
var orbitalKind = makeIdKind("orbital");
var entityKind = makeIdKind("entity");
var traitKind = makeIdKind("trait");
var eventKind = makeIdKind("event");
var pageKind = makeIdKind("page");
var serviceKind = makeIdKind("service");
var themeKind = makeIdKind("theme");
var paletteKind = makeIdKind("palette");
var isOrbitalId = orbitalKind.is;
var isEntityId = entityKind.is;
var isTraitId = traitKind.is;
var isEventId = eventKind.is;
var isPageId = pageKind.is;
var isServiceId = serviceKind.is;
var isThemeId = themeKind.is;
var isPaletteEntryId = paletteKind.is;
z.string().refine(isOrbitalId, { message: `Expected an orbital id (prefix "${ID_PREFIXES.orbital}")` });
var EntityIdSchema = z.string().refine(isEntityId, { message: `Expected an entity id (prefix "${ID_PREFIXES.entity}")` });
var TraitIdSchema = z.string().refine(isTraitId, { message: `Expected a trait id (prefix "${ID_PREFIXES.trait}")` });
z.string().refine(isEventId, { message: `Expected an event id (prefix "${ID_PREFIXES.event}")` });
z.string().refine(isPageId, { message: `Expected a page id (prefix "${ID_PREFIXES.page}")` });
z.string().refine(isServiceId, { message: `Expected a service id (prefix "${ID_PREFIXES.service}")` });
z.string().refine(isThemeId, { message: `Expected a theme id (prefix "${ID_PREFIXES.theme}")` });
z.string().refine(isPaletteEntryId, { message: `Expected a palette-entry id (prefix "${ID_PREFIXES.palette}")` });
var LedgerKindSchema = z.enum([
  "orbital",
  "entity",
  "trait",
  "event",
  "page",
  "service",
  "theme"
]);
var LedgerEntrySchema = z.object({
  id: z.string(),
  kind: LedgerKindSchema,
  bakedName: z.string(),
  curName: z.string(),
  renames: z.array(
    z.object({ from: z.string(), to: z.string(), at: z.string() })
  ),
  owner: z.enum(["std", "io", "workspace"]),
  parent: TraitIdSchema.optional()
});
z.object({
  schemaVersion: z.literal(1),
  entries: z.record(LedgerEntrySchema)
});
var JsonValueSchema = z.lazy(
  () => z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValueSchema), z.record(JsonValueSchema)])
);

// src/types/field.ts
var FIELD_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
  "timestamp",
  "datetime",
  "email",
  "url",
  "phone",
  "uuid",
  "image",
  "money",
  "file",
  "array",
  "object",
  "enum",
  "relation",
  "trait",
  "slot",
  "pattern",
  "node",
  "event",
  "scalar",
  "union"
];
z.enum(FIELD_TYPES);
var RelationCardinalitySchema = z.enum([
  "one",
  "many",
  "one-to-many",
  "many-to-one",
  "many-to-many"
]);
var RelationConfigSchema = z.object({
  entity: z.string().min(1, "Target entity is required"),
  entityId: EntityIdSchema.optional(),
  field: z.string().optional(),
  cardinality: RelationCardinalitySchema.optional(),
  onDelete: z.enum(["cascade", "nullify", "restrict"]).optional(),
  // Legacy compatibility fields
  foreignKey: z.string().optional(),
  target: z.string().optional(),
  type: RelationCardinalitySchema.optional()
}).transform((data) => {
  const normalized = {
    entity: data.entity || data.target || "",
    entityId: data.entityId,
    cardinality: data.cardinality || data.type,
    field: data.field,
    onDelete: data.onDelete
  };
  return normalized;
});
z.object({
  name: z.string(),
  url: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number()
});
var FIELD_TYPE_ALIASES = {
  text: "string",
  int: "number",
  float: "number",
  ts: "timestamp"
};
var EntityFieldSchema = z.lazy(() => {
  const baseFieldShape = {
    name: z.string().min(1, "Field name is required").optional(),
    required: z.boolean().optional(),
    primaryKey: z.boolean().optional(),
    default: JsonValueSchema.optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    properties: z.record(EntityFieldSchema).optional(),
    intrinsic: z.boolean().optional(),
    description: z.string().optional(),
    synonyms: z.string().optional(),
    mock: z.string().optional(),
    mergedFrom: z.string().optional()
  };
  function scalarVariant(t) {
    return z.object({
      ...baseFieldShape,
      type: z.literal(t),
      values: z.array(z.string()).optional()
    });
  }
  return z.preprocess(
    (input) => {
      if (input === null || typeof input !== "object" || !("type" in input) || typeof input.type !== "string") {
        return input;
      }
      const obj = input;
      const next = { ...obj };
      const aliased = FIELD_TYPE_ALIASES[obj.type];
      if (aliased !== void 0) next.type = aliased;
      if (next.enum !== void 0 && next.values === void 0) {
        next.values = next.enum;
      }
      delete next.enum;
      return next;
    },
    z.discriminatedUnion("type", [
      scalarVariant("string"),
      scalarVariant("number"),
      scalarVariant("boolean"),
      scalarVariant("date"),
      scalarVariant("timestamp"),
      scalarVariant("datetime"),
      scalarVariant("email"),
      scalarVariant("url"),
      scalarVariant("phone"),
      scalarVariant("uuid"),
      scalarVariant("image"),
      scalarVariant("trait"),
      scalarVariant("slot"),
      scalarVariant("pattern"),
      scalarVariant("node"),
      scalarVariant("event"),
      scalarVariant("money"),
      scalarVariant("file"),
      scalarVariant("scalar"),
      // Enum variant — REQUIRES non-empty values.
      z.object({
        ...baseFieldShape,
        type: z.literal("enum"),
        values: z.array(z.string()).min(1, "Enum field requires a non-empty `values` array")
      }),
      // Union variant — tagged struct union; values = variant names,
      // per-variant shapes in `properties` (absent on named recursion).
      z.object({
        ...baseFieldShape,
        type: z.literal("union"),
        values: z.array(z.string()).min(1, "Union field requires a non-empty `values` array")
      }),
      // Relation variant — REQUIRES relation config.
      z.object({
        ...baseFieldShape,
        type: z.literal("relation"),
        relation: RelationConfigSchema
      }),
      // Array variant — items optional to match relaxed TS shape.
      z.object({
        ...baseFieldShape,
        type: z.literal("array"),
        items: EntityFieldSchema.optional()
      }),
      // Object variant — fixed-key struct (`properties`) or dynamic-key
      // map (`Map K V`, uniform value schema in `items`).
      z.object({
        ...baseFieldShape,
        type: z.literal("object"),
        items: EntityFieldSchema.optional(),
        values: z.array(z.string()).optional()
      })
    ])
  );
});
var ENTITY_ROLES = [
  "player",
  "enemy",
  "npc",
  "item",
  "tile",
  "projectile",
  "effect",
  "ui",
  "decoration",
  "vehicle"
];
z.enum(ENTITY_ROLES);
var VISUAL_STYLES = ["pixel", "vector", "hd", "1-bit", "isometric"];
var VisualStyleSchema = z.enum(VISUAL_STYLES);
var ASSET_DIMENSIONS = ["2d", "3d"];
var AssetDimensionSchema = z.enum(ASSET_DIMENSIONS);
var ASSET_ASPECTS = ["1:1", "16:9", "5:7", "8:1", "4:1", "5:3", "1:2"];
var AssetAspectSchema = z.enum(ASSET_ASPECTS);
var ANIMATION_NAMES = ["idle", "walk", "skate", "jump", "fall", "attack", "hit", "death"];
z.enum(ANIMATION_NAMES);
var SPRITE_DIRECTIONS = ["se", "sw", "ne", "nw"];
var SpriteDirectionSchema = z.enum(SPRITE_DIRECTIONS);
var SHEET_PROJECTIONS = ["iso", "front", "back", "top", "perspective"];
z.enum(SHEET_PROJECTIONS);
var AnimationDefSchema = z.object({
  row: z.number().int().nonnegative(),
  frames: z.number().int().positive(),
  frameRate: z.number().positive(),
  loop: z.boolean()
});
z.object({
  unit: z.string().optional(),
  type: z.string().optional(),
  frameWidth: z.number().positive(),
  frameHeight: z.number().positive(),
  columns: z.number().int().positive(),
  rows: z.number().int().positive(),
  directions: z.array(SpriteDirectionSchema),
  sheets: z.record(SpriteDirectionSchema, z.string()),
  animations: z.record(z.string(), AnimationDefSchema),
  projections: z.object({
    iso: z.record(SpriteDirectionSchema, z.string()).optional(),
    front: z.string().optional(),
    back: z.string().optional(),
    top: z.string().optional(),
    perspective: z.string().optional()
  }).optional()
});
var SubTextureSchema = z.object({
  x: z.number().nonnegative(),
  y: z.number().nonnegative(),
  width: z.number().positive(),
  height: z.number().positive(),
  frameX: z.number().optional(),
  frameY: z.number().optional(),
  frameWidth: z.number().positive().optional(),
  frameHeight: z.number().positive().optional()
});
z.object({
  imagePath: z.string(),
  subTextures: z.record(z.string(), SubTextureSchema)
});
z.object({
  imagePath: z.string(),
  tileWidth: z.number().positive(),
  tileHeight: z.number().positive(),
  columns: z.number().int().positive(),
  rows: z.number().int().positive(),
  margin: z.number().nonnegative().optional(),
  spacing: z.number().nonnegative().optional(),
  names: z.array(z.string()).optional()
});
var SemanticAssetRefSchema = z.object({
  role: z.string().min(1),
  category: z.string().min(1),
  animations: z.array(z.string()).optional(),
  style: VisualStyleSchema.optional(),
  variant: z.string().optional(),
  dimension: AssetDimensionSchema.optional(),
  aspect: AssetAspectSchema.optional()
});
SemanticAssetRefSchema.extend({
  url: z.string(),
  atlas: z.string().optional(),
  sprite: z.string().optional(),
  name: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  prompt: z.string().optional(),
  choreography: z.record(z.string(), z.array(z.string())).optional()
});
var AssetCatalogEntrySchema = z.object({
  url: z.string(),
  name: z.string(),
  category: z.string(),
  kind: z.enum(["image", "spritesheet", "audio", "scene", "portrait", "model", "other"]),
  thumbnailUrl: z.string().optional(),
  dimension: AssetDimensionSchema.optional(),
  aspect: AssetAspectSchema.optional()
});
z.array(AssetCatalogEntrySchema);
var ScenePosSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().optional()
});
var CAMERA_MODES = ["isometric", "perspective", "top-down", "front", "follow", "chase"];
var CameraModeSchema = z.enum(CAMERA_MODES);
z.object({
  pos: ScenePosSchema.optional(),
  target: ScenePosSchema.optional(),
  zoom: z.number().optional(),
  fov: z.number().optional(),
  mode: CameraModeSchema.optional(),
  azimuth: z.number().optional(),
  elevation: z.number().optional()
});
var MANIFEST_ENTRY_KINDS = ["model", "image", "spritesheet", "audio", "json"];
var ManifestEntryKindSchema = z.enum(MANIFEST_ENTRY_KINDS);
var SoundEntrySchema = z.object({
  path: z.union([z.string(), z.array(z.string())]),
  volume: z.number().optional(),
  loop: z.boolean().optional(),
  poolSize: z.number().optional(),
  autostart: z.boolean().optional(),
  crossfade: z.boolean().optional(),
  crossfadeDurationMs: z.number().optional()
});
z.record(z.string(), SoundEntrySchema);
var MANIFEST_CANVAS_AFFINITIES = ["isometric", "hex", "flat", "side", "3d", "none"];
var ManifestCanvasAffinitySchema = z.enum(MANIFEST_CANVAS_AFFINITIES);
var MANIFEST_SOURCE_CATALOGS = ["kenny", "kekec-assets", "iram-assets", "trait-wars-assets", "kflow-assets"];
var ManifestSourceCatalogSchema = z.enum(MANIFEST_SOURCE_CATALOGS);
var MANIFEST_ASSET_LICENSES = ["CC0", "proprietary"];
var ManifestAssetLicenseSchema = z.enum(MANIFEST_ASSET_LICENSES);
var ManifestFrameSpecSchema = z.union([
  z.object({ kind: z.literal("iso-tile"), w: z.number(), h: z.number() }),
  z.object({ kind: z.literal("sprite-sheet"), frame: z.number(), cols: z.number(), rows: z.number() }),
  z.string().min(1),
  z.null()
]);
z.object({
  url: z.string(),
  name: z.string(),
  category: z.string(),
  kind: ManifestEntryKindSchema,
  width: z.number(),
  height: z.number(),
  canvasAffinity: ManifestCanvasAffinitySchema,
  genreAffinity: z.array(z.string()),
  swapClass: z.string(),
  sourceCatalog: ManifestSourceCatalogSchema,
  frameSpec: ManifestFrameSpecSchema,
  license: ManifestAssetLicenseSchema.optional()
});
var SExprDataSchema = z.lazy(
  () => z.union([SExprAtomSchema, z.array(SExprDataSchema)])
);
var SExprAtomSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.record(SExprDataSchema)
  // Objects for payload data
]);
var SExprSchema = z.lazy(
  () => z.union([
    SExprAtomSchema,
    z.array(z.lazy(() => SExprSchema)).min(1).refine(
      (arr) => typeof arr[0] === "string",
      { message: "S-expression array must have a string operator as first element" }
    )
  ])
);
var EventPayloadValueSchema = z.lazy(
  () => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.null(),
    z.undefined(),
    EventPayloadSchema,
    z.array(EventPayloadValueSchema)
  ])
);
var EventPayloadSchema = z.lazy(() => z.record(EventPayloadValueSchema));

// src/types/entity.ts
var EntityPersistenceSchema = z.enum([
  "persistent",
  "runtime"
]);
z.object({
  name: z.string().min(1, "Entity name is required"),
  // V4 arena id (`EntityDefinition.id`); declared so the strip-mode zod gate
  // carries an imported entity's id through instead of erasing it.
  id: EntityIdSchema.optional(),
  persistence: EntityPersistenceSchema.default("persistent"),
  shared: z.boolean().optional(),
  // Must stay in step with the Rust serde field (`EntityDefinition.identity`,
  // orbital-core/src/schema/types.rs). `z.object()` STRIPS unknown keys rather
  // than rejecting them, so a Rust-only field works on the compiled path and
  // silently vanishes on the interpreter path.
  identity: z.boolean().optional(),
  // Same "must stay in step with Rust serde" hazard as `identity` above —
  // these four mirror `EntityDefinition.{read,create,update,delete}_policy`.
  // Rust has no `rename_all` on this struct, so the wire key IS snake_case
  // (unlike this file's other camelCase fields, which are Rust-side-absent
  // and so never hit this cross-language constraint — see `visual_prompt`
  // just below for the existing precedent).
  read_policy: SExprSchema.optional(),
  create_policy: SExprSchema.optional(),
  update_policy: SExprSchema.optional(),
  delete_policy: SExprSchema.optional(),
  // Snake_case for the same cross-language reason as the four policies above.
  access_waivers: z.object({
    read: z.string().optional(),
    create: z.string().optional(),
    update: z.string().optional(),
    delete: z.string().optional()
  }).optional(),
  collection: z.string().optional(),
  fields: z.array(EntityFieldSchema).min(1, "At least one field is required"),
  instances: z.array(z.record(JsonValueSchema)).optional(),
  timestamps: z.boolean().optional(),
  softDelete: z.boolean().optional(),
  description: z.string().optional(),
  visual_prompt: z.string().optional(),
  assetRef: SemanticAssetRefSchema.optional()
});
var FieldValueSchema = z.lazy(
  () => z.union([z.string(), z.number(), z.boolean(), z.date(), z.null(), z.array(FieldValueSchema), z.record(FieldValueSchema.optional())])
);
function isFieldValue(value) {
  if (value === null) return true;
  const kind = typeof value;
  if (kind === "string" || kind === "number" || kind === "boolean") return true;
  if (value instanceof Date) return true;
  if (Array.isArray(value)) return value.every((item) => isFieldValue(item));
  if (kind === "object" && value !== null && typeof value === "object") {
    return Object.values(value).every((item) => item === void 0 || isFieldValue(item));
  }
  return false;
}
z.object({ id: z.string().optional() }).catchall(FieldValueSchema.optional());

// src/mock/sampleValue.ts
var MAX_NESTED_DEPTH = 3;
var RESERVED_FIELD_NAMES = /* @__PURE__ */ new Set(["id", "createdAt", "updatedAt"]);
var IMAGE_FIELD_NAMES = /* @__PURE__ */ new Set([
  "image",
  "imageurl",
  "image_url",
  "photo",
  "photourl",
  "photo_url",
  "avatar",
  "avatarurl",
  "avatar_url",
  "thumbnail",
  "thumbnailurl",
  "thumbnail_url",
  "picture",
  "pictureurl",
  "cover",
  "coverurl",
  "banner",
  "bannerurl"
]);
function sampleImageUrl(entityName, fieldName, ctx, width = 400, height = 400) {
  const salt = ctx.strategy === "seeded" ? randomInt({ min: 0, max: 1e3 }) : ctx.index;
  const seed = `${entityName}-${fieldName}-${salt}`;
  return `https://picsum.photos/seed/${encodeURIComponent(seed)}/${width}/${height}`;
}
function titleCase(name) {
  const spaced = name.split("").flatMap((c) => c === "_" || c === "-" ? [" "] : c === c.toUpperCase() && c !== c.toLowerCase() ? [" ", c] : [c]).join("");
  return spaced.split(/\s+/).filter((w) => w.length > 0).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function slug(name) {
  return name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() || "value";
}
function indexUuid(index) {
  return `00000000-0000-4000-8000-${index.toString(16).padStart(12, "0").slice(-12)}`;
}
function declaredValues(field) {
  if (field.type === "union") return void 0;
  const values = "values" in field ? field.values : void 0;
  return values && values.length > 0 ? values : void 0;
}
function isDeclaredDefaultHonored(field, isRuntime = false) {
  const value = field.default;
  if (value === void 0 || value === null) return false;
  if (isRuntime) return true;
  if (typeof value === "number" || typeof value === "boolean") return false;
  if (Array.isArray(value)) return true;
  if (typeof value === "object") return true;
  if (typeof value !== "string") return false;
  const values = declaredValues(field);
  if (value === "") return values !== void 0 && values.includes("");
  return values === void 0;
}
function sampleRowCount(entity, requested) {
  return entity.persistence === "runtime" ? 1 : requested;
}
function mockFieldValue(declared, index) {
  const candidates = declared.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
  if (candidates.length > 1) {
    return candidates[(index - 1) % candidates.length];
  }
  return `${declared.trim()}-${index}`;
}
function sampleText(field, ctx) {
  const fieldName = field.name ?? "field";
  if (IMAGE_FIELD_NAMES.has(fieldName.toLowerCase())) {
    return sampleImageUrl(ctx.entityName, fieldName, ctx);
  }
  return ctx.strategy === "seeded" ? titleCase(randomWords(2)) : `${titleCase(fieldName)} ${ctx.index}`;
}
function sampleDate(ctx, dateOnly) {
  if (ctx.strategy === "index") {
    const month = String(ctx.index % 12 + 1).padStart(2, "0");
    return dateOnly ? `2026-${month}-15` : `2026-${month}-15T00:00:00.000Z`;
  }
  const iso = randomStraddlingDate({ days: 15 }).toISOString();
  return dateOnly ? iso.split("T")[0] : iso;
}
function sampleRelation(field) {
  return field.relation?.cardinality === "one" ? "" : [];
}
function sampleArray(field, ctx) {
  const depth = ctx.depth ?? 0;
  if (!field.items || depth >= MAX_NESTED_DEPTH) return [];
  const count = ctx.strategy === "seeded" ? randomInt({ min: 3, max: 5 }) : 3;
  const elementName = field.name ?? "item";
  const out = [];
  for (let i = 0; i < count; i++) {
    const element = { ...field.items, name: `${elementName}[${i}]` };
    out.push(
      sampleFieldValue(element, {
        ...ctx,
        index: ctx.index * 10 + i,
        depth: depth + 1
      }) ?? null
    );
  }
  return out;
}
function sampleObject(field, ctx) {
  const depth = ctx.depth ?? 0;
  if (!field.properties || depth >= MAX_NESTED_DEPTH) return null;
  const out = {};
  for (const [propName, propField] of Object.entries(field.properties)) {
    const child = { ...propField, name: propName };
    out[propName] = sampleFieldValue(child, { ...ctx, depth: depth + 1 }) ?? null;
  }
  return out;
}
function sampleUnion(field, ctx) {
  const depth = ctx.depth ?? 0;
  if (!field.properties || field.values.length === 0 || depth >= MAX_NESTED_DEPTH) {
    return void 0;
  }
  const firstVariant = field.properties[field.values[0]];
  if (!firstVariant) return void 0;
  const child = { ...firstVariant, name: field.name };
  return sampleFieldValue(child, { ...ctx, depth: depth + 1 });
}
function omitsUndefaultedOptionalFields(ctx) {
  return ctx.strategy === "seeded" && ctx.index % 2 === 0;
}
function sampleFieldValue(field, ctx) {
  const honoredDefault = field.default !== void 0 && isFieldValue(field.default) ? field.default : void 0;
  if (field.intrinsic === true) return honoredDefault;
  const isRuntime = ctx.persistence === "runtime";
  if (isDeclaredDefaultHonored(field, isRuntime) && honoredDefault !== void 0) {
    return honoredDefault;
  }
  if (!field.required && field.default === void 0 && field.type !== "relation" && omitsUndefaultedOptionalFields(ctx)) {
    return void 0;
  }
  const values = declaredValues(field);
  if (values) {
    const ordinal = isRuntime ? 1 : ctx.index;
    return values[(ordinal - 1) % values.length];
  }
  if (field.mock !== void 0) {
    const ordinal = isRuntime ? 1 : ctx.index;
    return mockFieldValue(field.mock, ordinal);
  }
  switch (field.type) {
    case "string":
      return sampleText(field, ctx);
    // Semantic domains synthesize a value that SATISFIES their own validator —
    // the property that keeps the seeder and the validator from drifting.
    case "email":
      return ctx.strategy === "seeded" ? randomEmail() : `${slug(field.name ?? "user")}${ctx.index}@example.com`;
    case "url":
      return ctx.strategy === "seeded" ? randomUrl() : `https://example.com/${slug(field.name ?? "link")}/${ctx.index}`;
    case "phone":
      return ctx.strategy === "seeded" ? randomPhone() : `+1-555-${String(1e3 + ctx.index % 9e3).padStart(4, "0")}`;
    case "uuid":
      return ctx.strategy === "seeded" ? randomUuid() : indexUuid(ctx.index);
    case "image":
      return sampleImageUrl(ctx.entityName, field.name ?? "image", ctx);
    case "number": {
      if (ctx.strategy === "seeded") {
        return randomInt({ min: field.min ?? 1, max: field.max ?? 250 });
      }
      const stepped = (field.min ?? 0) + ctx.index * 10;
      return field.max !== void 0 && stepped > field.max ? field.max : stepped;
    }
    // Money is numeric at rest — same sampling as `number` (index strategy
    // mirrors the Rust seeder for the shared parity fixture). Seeded fallback
    // avoids $0.00 amounts for the same reason as `number`.
    case "money": {
      if (ctx.strategy === "seeded") {
        return randomInt({ min: field.min ?? 10, max: field.max ?? 900 });
      }
      const steppedMoney = (field.min ?? 0) + ctx.index * 10;
      return field.max !== void 0 && steppedMoney > field.max ? field.max : steppedMoney;
    }
    // File seeds the canonical struct value (satisfies isFileValue) —
    // mirrors the Rust seeder's FieldType::File arm byte-for-byte.
    case "file": {
      const base = slug(field.name ?? "file");
      return {
        name: `${base}-${ctx.index}.pdf`,
        url: `https://example.com/files/${base}/${ctx.index}.pdf`,
        mimeType: "application/pdf",
        sizeBytes: ctx.index * 1024
      };
    }
    case "boolean":
      return ctx.strategy === "seeded" ? randomBoolean() : ctx.index % 2 === 0;
    case "date":
      return sampleDate(ctx, true);
    case "timestamp":
    case "datetime":
      return sampleDate(ctx, false);
    case "enum":
      return null;
    case "relation":
      return sampleRelation(field);
    case "array":
      return sampleArray(field, ctx);
    case "object":
      return sampleObject(field, ctx);
    case "union":
      return sampleUnion(field, ctx);
    case "trait":
    case "slot":
    case "pattern":
    // Renderable content is computed by the owning trait's effects, never
    // seeded — same no-sample treatment as the config-only reference types.
    case "node":
      return void 0;
    default:
      return sampleText(field, ctx);
  }
}
function sampleRow(entity, ctx) {
  const row = {};
  for (const field of entity.fields) {
    const name = field.name;
    if (!name || RESERVED_FIELD_NAMES.has(name)) continue;
    const value = sampleFieldValue(field, {
      ...ctx,
      entityName: entity.name,
      persistence: ctx.persistence ?? entity.persistence
    });
    if (value !== void 0) row[name] = value;
  }
  return row;
}
function sampleRows(entity, count, strategy) {
  const rowCount = sampleRowCount(entity, count);
  const rows = [];
  for (let i = 1; i <= rowCount; i++) {
    rows.push(sampleRow(entity, { index: i, strategy, persistence: entity.persistence }));
  }
  return rows;
}

// src/mock/relationForest.ts
function selfRelationForest(rowIds) {
  return {
    parentOf: (index) => index === 0 ? "" : rowIds[Math.floor((index - 1) / 2)] ?? "",
    childrenOf: (index) => {
      const children = [];
      const left = 2 * index + 1;
      const right = 2 * index + 2;
      if (left < rowIds.length) children.push(rowIds[left]);
      if (right < rowIds.length) children.push(rowIds[right]);
      return children;
    }
  };
}
var SCALAR_PARENT_CARDINALITIES = /* @__PURE__ */ new Set(["one", "many-to-one"]);
function linkSelfRelationField(rows, field, isExcluded) {
  const rowIds = rows.map((row) => row.id ?? "");
  const forest = selfRelationForest(rowIds);
  const scalar = SCALAR_PARENT_CARDINALITIES.has(field.cardinality ?? "many");
  rows.forEach((row, index) => {
    if (isExcluded?.(row, index)) return;
    row[field.name] = scalar ? forest.parentOf(index) : forest.childrenOf(index);
  });
}

export { IMAGE_FIELD_NAMES, RESERVED_FIELD_NAMES, collectUserFieldLiterals, entityAccessPolicies, entityAccessPoliciesByStoreKey, entityAccessTable, identityEntitiesOf, identityEntityName, identityEntityNames, isDeclaredDefaultHonored, linkSelfRelationField, ownerColumnsFromPolicy, ownerFieldsFromSchema, randomAnytimeDate, randomArrayElement, randomBoolean, randomColor, randomEmail, randomFloat, randomInt, randomPassword, randomPastDate, randomPhone, randomRecentDate, randomSentence, randomStraddlingDate, randomUrl, randomUuid, randomWords, roleSatisfyingPolicy, roleVocabularyOf, sampleFieldValue, sampleImageUrl, sampleRow, sampleRowCount, sampleRows, seedRandom, selfRelationForest, shuffleArray };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map